import React, { useState, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, TextField, MenuItem, IconButton, Tooltip,
    Dialog, DialogTitle, DialogContent, DialogActions, Grid, Divider, Alert,
} from '@mui/material';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { solidBtn, ghostBtn, field } from '../uiKit';
import { fmtDate } from '../../utils/format';

const pad = (n) => String(n).padStart(2, '0');
const toYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const localToday = () => toYmd(new Date());

export const addDays = (ymd, n) => {
    const d = new Date(`${ymd}T00:00`);
    d.setDate(d.getDate() + n);
    return toYmd(d);
};

const daysUntil = (ymd) => Math.round((new Date(`${ymd}T00:00`) - new Date(`${localToday()}T00:00`)) / 864e5);

const dueLabel = (n) => {
    if (n < 0) return `Overdue by ${-n} day${n === -1 ? '' : 's'}`;
    if (n === 0) return 'Due today';
    if (n === 1) return 'Due tomorrow';
    return `Due in ${n} days`;
};

// How many reminder cards sit on the page itself; the rest open in a list dialog.
const INLINE_REMINDERS = 2;

function ReminderCard({ r, onDismiss, compact }) {
    const late = r.days < 0;
    const tint = late ? '#E11D48' : '#EBA84E';
    return (
        <Stack
            direction="row" spacing={1.5}
            sx={{ alignItems: 'center', p: compact ? 1.2 : 1.5, borderRadius: '10px', bgcolor: late ? '#FDEBEE' : '#FEF3E2', border: `1px solid ${tint}40` }}
        >
            <Box sx={{ width: 36, height: 36, borderRadius: '9px', bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <NotificationsActiveRoundedIcon sx={{ fontSize: 20, color: tint }} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#0F172A' }} noWrap>{r.category}</Typography>
                <Typography sx={{ fontSize: 12, color: '#64748B' }} noWrap>
                    {r.description ? `${r.description} · ` : ''}{fmtDate(r.dueDate)} ·{' '}
                    <Box component="span" sx={{ fontWeight: 700, color: late ? '#E11D48' : '#92580B' }}>{dueLabel(r.days)}</Box>
                </Typography>
            </Box>
            <Button size="small" onClick={() => onDismiss(r.id)} sx={{ textTransform: 'none', fontWeight: 700 }}>Dismiss</Button>
        </Stack>
    );
}

/**
 * Notification-style cards for the reminders the server reports as active (their remind window has
 * started). Overdue ones stay until dismissed; nothing expires on its own. The first few show on the
 * page; when there are more, a "View all" button opens them all in a dialog, overdue first.
 */
export function DueReminders({ reminders, onDismiss }) {
    const [allOpen, setAllOpen] = useState(false);
    const due = useMemo(() => (
        [...reminders]
            .map((r) => ({ ...r, days: r.daysRemaining ?? daysUntil(r.dueDate) }))
            .sort((x, y) => x.days - y.days)
    ), [reminders]);
    if (!due.length) return null;

    const inline = due.slice(0, INLINE_REMINDERS);
    const extra = due.length - inline.length;
    const overdue = due.filter((r) => r.days < 0).length;

    return (
        <>
            <Stack spacing={1} sx={{ mb: 2.5 }}>
                {inline.map((r) => <ReminderCard key={r.id} r={r} onDismiss={onDismiss} />)}
                {extra > 0 && (
                    <Button
                        onClick={() => setAllOpen(true)} startIcon={<NotificationsActiveRoundedIcon />}
                        sx={{ alignSelf: 'flex-start', textTransform: 'none', fontWeight: 700, color: '#92580B', bgcolor: '#FEF3E2', border: '1px solid #EBA84E40', borderRadius: '8px', px: 1.8, '&:hover': { bgcolor: '#FDE9C8' } }}
                    >
                        View all {due.length} reminders (+{extra} more)
                    </Button>
                )}
            </Stack>

            <Dialog open={allOpen} onClose={() => setAllOpen(false)} fullWidth maxWidth="sm" slotProps={{ paper: DIALOG_PAPER }}>
                <DialogTitle sx={dialogTitleSx}>
                    Reminders ({due.length})
                    <Typography sx={{ fontSize: 12.5, fontWeight: 500, color: '#64748B', mt: 0.3 }}>
                        {overdue > 0 ? `${overdue} overdue · ` : ''}{due.length - overdue} due soon
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ ...dialogBodySx, maxHeight: '60vh' }}>
                    <Stack spacing={1}>
                        {due.map((r) => <ReminderCard key={r.id} r={r} onDismiss={onDismiss} compact />)}
                    </Stack>
                </DialogContent>
                <DialogActions sx={dialogActionsSx}>
                    <Button onClick={() => setAllOpen(false)} sx={ghostBtn}>Close</Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

const DIALOG_PAPER = { sx: { borderRadius: '12px' } };
const dialogTitleSx = { fontWeight: 800, fontSize: 18, px: 3, pt: 2.5, pb: 1.5 };
const dialogBodySx = { px: 3, pt: '10px !important', pb: 2 };
const dialogActionsSx = { px: 3, py: 2, gap: 1, borderTop: '1px solid #EEF0F6' };

/**
 * Remind about a saved expense category: due in N days from today, remind me M days before. The
 * server fixes the due date when the reminder is saved. Also lists and removes every saved reminder.
 * `categories` must be saved ones: a reminder never creates a category (the server answers 404).
 * `onAdd` returns a promise that rejects with the server message.
 */
export function ReminderDialog({ open, onClose, categories, reminders, onAdd, onDismiss }) {
    const [category, setCategory] = useState('');
    const [dueDays, setDueDays] = useState('');
    const [daysBefore, setDaysBefore] = useState('2');
    const [description, setDescription] = useState('');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');

    const lead = Math.max(0, Math.floor(Number(daysBefore) || 0));
    const chosen = categories.includes(category) ? category : (categories[0] || '');
    // The due date is counted in days from today (the day the reminder is saved), not picked.
    const dueIn = Math.max(0, Math.floor(Number(dueDays) || 0));
    const dueDate = dueIn > 0 ? addDays(localToday(), dueIn) : '';
    const startsOn = dueDate ? addDays(dueDate, -lead) : null;
    const canSave = chosen && dueIn > 0 && daysBefore !== '' && !saving;

    const save = async () => {
        setSaving(true);
        setErr('');
        try {
            await onAdd({ category: chosen, dueInDays: dueIn, remindBeforeDays: lead, description: description.trim() });
            setDueDays('');
            setDescription('');
        } catch (e) {
            setErr(e.message || 'Could not set the reminder.');
        } finally {
            setSaving(false);
        }
    };
    const sorted = useMemo(() => [...reminders].sort((a, b) => a.dueDate.localeCompare(b.dueDate)), [reminders]);

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: DIALOG_PAPER }}>
            <DialogTitle sx={dialogTitleSx}>Set Reminder</DialogTitle>
            <DialogContent sx={dialogBodySx}>
                <Grid container columnSpacing={2.5} rowSpacing={2}>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            select label="Category" fullWidth size="small" value={chosen} onChange={(e) => setCategory(e.target.value)} sx={field}
                            helperText={categories.length ? 'The expense category to remind about' : 'Save an expense with a category first'}
                            disabled={!categories.length}
                        >
                            {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                        </TextField>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                            label="Due in … days" type="number" required fullWidth size="small" value={dueDays}
                            onChange={(e) => setDueDays(e.target.value)} sx={field} slotProps={{ htmlInput: { min: 1 } }}
                            helperText={dueDate ? `From today (${fmtDate(localToday())}) — due on ${fmtDate(dueDate)}` : 'e.g. 18'}
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField
                            label="Remind me … days before" type="number" fullWidth size="small" value={daysBefore}
                            onChange={(e) => setDaysBefore(e.target.value)} sx={field} slotProps={{ htmlInput: { min: 0 } }}
                            helperText={startsOn ? `You'll be reminded from ${fmtDate(startsOn)}${lead === 0 ? ' (the day itself)' : ''}` : 'Starts showing this many days before the due date'}
                        />
                    </Grid>
                    <Grid size={12}>
                        <TextField label="Description" placeholder="Optional - what is this reminder for?" fullWidth size="small" multiline minRows={2}
                            value={description} onChange={(e) => setDescription(e.target.value)} sx={field} />
                    </Grid>
                    {err && <Grid size={12}><Alert severity="error">{err}</Alert></Grid>}
                </Grid>

                {sorted.length > 0 && (
                    <>
                        <Divider sx={{ my: 2.5 }} />
                        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: '#6E6B99', letterSpacing: 0.6, mb: 1 }}>ALL REMINDERS</Typography>
                        <Stack spacing={0.5}>
                            {sorted.map((r) => (
                                <Stack key={r.id} direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
                                    <Typography sx={{ fontSize: 13 }}>
                                        <b>{r.category}</b> · due {fmtDate(r.dueDate)} · {r.remindBeforeDays}d before{r.status ? ` · ${r.status}` : ''}{r.description ? ` · ${r.description}` : ''}
                                    </Typography>
                                    <Tooltip title="Remove">
                                        <IconButton size="small" onClick={() => onDismiss(r.id)}><DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} /></IconButton>
                                    </Tooltip>
                                </Stack>
                            ))}
                        </Stack>
                    </>
                )}
            </DialogContent>
            <DialogActions sx={dialogActionsSx}>
                <Button onClick={onClose} sx={ghostBtn}>Close</Button>
                <Button onClick={save} disabled={!canSave} sx={solidBtn}>{saving ? 'Saving…' : 'Set Reminder'}</Button>
            </DialogActions>
        </Dialog>
    );
}

export { DIALOG_PAPER, dialogTitleSx, dialogBodySx, dialogActionsSx };
